import { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router';

import { DoctorSimple } from '@dreryk/egabinet-personnel-frontend-api';
import { PractitionerId } from '@dreryk/egabinet-personnel-published-language-api';

import { createSingleInterpolatedTranslationKey, useTranslate } from '@dreryk/react-common';

import { visitPaths } from '~libs.desktop.shared.paths';

import { appointmentsTranslationKeys } from '../../../../../translations';

import { doctorsFilterSessionStorage } from './DoctorsFilter.storage';

export const useToggleLabelExtractor = (
   healthcareProviderDoctors: DoctorSimple[] | undefined
): ((doctors: DoctorSimple[]) => string) => {
   const translate = useTranslate();
   return useCallback(
      (doctors) => {
         if (!doctors.length) {
            return translate(appointmentsTranslationKeys.filters.chooseSpecialists);
         }
         if (doctors.length === healthcareProviderDoctors?.length) {
            return translate(appointmentsTranslationKeys.filters.allSpecialistsChosen);
         }
         if (doctors.length === 1) {
            const [defaultDoctor] = doctors;
            return `${defaultDoctor.lastName} ${defaultDoctor.firstName}`;
         }
         return translate(
            createSingleInterpolatedTranslationKey(
               appointmentsTranslationKeys.filters.chosenSpecialists,
               'doctorsCount',
               doctors.length
            )
         );
      },
      [healthcareProviderDoctors, translate]
   );
};

export const DOCTORS_FILTER_FIELD_NAME = 'doctors';

export const useDoctorsFilterSearchParamHandler = (currentDoctorsFilter: DoctorSimple[]): VoidFunction => {
   const history = useHistory();
   const location = useLocation();

   return useCallback(() => {
      const searchParams = new URLSearchParams(location.search);
      if (currentDoctorsFilter.length) {
         const nextPractitionerIdsAsNumber = currentDoctorsFilter.map((doctor) => doctor.practitionerId);
         searchParams.set(visitPaths.APPOINTMENTS_PRACTITIONER_SEARCH_PARAM, nextPractitionerIdsAsNumber.join(','));
         doctorsFilterSessionStorage.setPractitionerIdsFilter(nextPractitionerIdsAsNumber.map(PractitionerId.of));
      }
      if (!currentDoctorsFilter.length) {
         searchParams.delete(visitPaths.APPOINTMENTS_PRACTITIONER_SEARCH_PARAM);
         doctorsFilterSessionStorage.removePractitionerIdsFilter();
      }
      history.replace({
         search: searchParams.toString(),
         state: location.state,
      });
   }, [history, location.search, location.state, currentDoctorsFilter]);
};
